import React, {useState} from 'react';
import {Col, Row} from 'react-bootstrap';
import Button from '@/components/ui/Button';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import axios from 'axios';
import {useAlert} from '@/components/alerts';

import usePaginatedData from '@/utils/usePaginatedData';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {buildEventDetailURL} from '@/utils/urlBuilder';
import TableComponent from '@/components/table/Table';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import {navigate} from '@reach/router';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {Tooltip} from 'components/tooltips';
import {buildURL} from '@/utils/urlBuilder';
import {extractErrorMessage} from '@/utils/errorHandling';

interface TranscriptionTask {
    id: number;
    eventId: number;
    datetime: string | null;
    lang: string | null;
    audioUri: string;
}

interface UserData {
    permissions?: string[];
}

const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const TranscriptionTasksNotSentListView: React.FC = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const {showSuccess, showError} = useAlert();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [loadingSubmitAll, setLoadingSubmitAll] = useState<boolean>(false);
    const [loadingRunMonitor, setLoadingRunMonitor] = useState<boolean>(false);
    const [loadingRunMonitorBatch, setLoadingRunMonitorBatch] = useState<boolean>(false);

    const {
        data: transcriptionTasksNotSent,
        loading: loadingTranscriptionTasksNotSent,
        currentPage,
        setCurrentPage,
        count,
        pages,
    } = usePaginatedData(window.URLS.transcriptionTasksNotSentResource, {
        storeResolver: 'transcriptionTasksNotSent',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const userCanAccessTranscriptionTasksNotSentResource = hasPermission('fs.transcription_task.list', userData?.permissions, true);
    const userCanSubmitAllTranscriptions = hasPermission('fs.transcription_task.syncronize.execute', userData?.permissions, true);
    const userCanRunMonitor = hasPermission('fs.transcription_task.syncronize.execute', userData?.permissions, true);

    if (!userCanAccessTranscriptionTasksNotSentResource && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_the_not.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const active = currentPage;
    let items: React.ReactNode;

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const detailUrlGenerator = (rowData: TranscriptionTask): string => {
        return buildEventDetailURL(rowData.eventId);
    };

    const handleSubmitAllTranscriptions = async (): Promise<void> => {
        if (!userCanSubmitAllTranscriptions) {return;}

        setLoadingSubmitAll(true);
        try {
            const response = await axios.post(buildURL('transcriptionProcessSubmitAll'));
            showSuccess(response.data.message || 'Transcriptions submitted successfully');
        } catch (error) {
            console.error('Error submitting transcriptions:', error);
            const errorMessage = extractErrorMessage(error, 'Error submitting transcriptions. Please try again.');
            showError(errorMessage);
        } finally {
            setLoadingSubmitAll(false);
        }
    };

    const handleRunMonitor = async (): Promise<void> => {
        if (!userCanRunMonitor) {return;}

        setLoadingRunMonitor(true);
        try {
            const response = await axios.get(buildURL('transcriptionProcessRunMonitor'));
            showSuccess(response.data.message || 'Monitor started successfully');
        } catch (error) {
            console.error('Error running monitor:', error);
            const errorMessage = extractErrorMessage(error, 'Error running monitor. Please try again.');
            showError(errorMessage);
        } finally {
            setLoadingRunMonitor(false);
        }
    };

    const handleRunMonitorBatch = async (): Promise<void> => {
        if (!userCanRunMonitor) {return;}

        setLoadingRunMonitorBatch(true);
        try {
            const response = await axios.get(buildURL('transcriptionProcessRunMonitorList'));
            showSuccess(response.data.message || 'Monitor batch started successfully');
        } catch (error) {
            console.error('Error running monitor batch:', error);
            const errorMessage = extractErrorMessage(error, 'Error running monitor batch. Please try again.');
            showError(errorMessage);
        } finally {
            setLoadingRunMonitorBatch(false);
        }
    };

    const columns = [
        {
            accessorKey: 'datetime',
            header: i18n.t('th_event_datetime.text'),
            cell: (cell: any) => {
                if (cell.getValue() === null) {
                    return '-';
                } else {
                    return cell.getValue();
                }
            },
        },
        {
            accessorKey: 'lang',
            header: i18n.t('th_lang.text'),
            cell: (cell: any) => {
                if (cell.getValue() === null) {
                    return '-';
                } else {
                    return cell.getValue();
                }
            },
        },
        {
            accessorKey: 'audioUri',
            header: i18n.t('th_audio_uri.text'),
            cell: (cell: any) => (
                <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />
            ),
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <>
                    <div className="d-inline-flex">
                        <Tooltip content={i18n.t('jt_view.text')} placement="top">
                            <Button
                                variant="outline-info"
                                size="sm"
                                icon="mdi-eye"
                                onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                            />
                        </Tooltip>

                        <Button
                            variant="outline-info"
                            size="sm"
                            style={{
                                fontSize: '12px',
                                padding: '0.5rem 1rem',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                        >
                            {i18n.t('jt_submit.text')}
                        </Button>
                    </div>
                </>
            ),
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_the_not.text')} />

            <Row>
                <Col lg={4}>
                    <p>
                        <b>{i18n.t('jt_calls_waiting.text')} </b>
                        {count}
                    </p>
                </Col>
            </Row>
            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
                <Col lg={9}>
                    <div className="d-flex justify-content-end align-items-center flex-wrap mb-2">
                        {userCanSubmitAllTranscriptions && (
                            <Button
                                variant="info"
                                size="sm"
                                className='mr-2'
                                onClick={handleSubmitAllTranscriptions}
                                disabled={loadingSubmitAll}                            >
                                {loadingSubmitAll ? 'Submitting...' : i18n.t('jt_submit_tasks.text')}
                            </Button>
                        )}
                        {userCanRunMonitor && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className='mr-2'
                                onClick={handleRunMonitor}
                                disabled={loadingRunMonitor}
                            >
                                {loadingRunMonitor ? 'Starting...' : i18n.t('jt_run_monitor.text')}
                            </Button>
                        )}
                        {userCanRunMonitor && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRunMonitorBatch}
                                disabled={loadingRunMonitorBatch}
                            >
                                {loadingRunMonitorBatch ? 'Starting...' : i18n.t('jt_run_monitor_in_batch.text')}
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>

            {loadingTranscriptionTasksNotSent ? (
                <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
            ) : (
                <>
                    <div className="card">
                        <div className="card-body">
                            <TableComponent data={transcriptionTasksNotSent} columns={columns} />
                        </div>
                    </div>
                    <PaginationComponent
                        pages={pages}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                </>
            )}
        </>
    );
});

export default TranscriptionTasksNotSentListView; 